import { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { BACKEND_URL } from '../config';

const PUBLIC = process.env.PUBLIC_URL || '';

const Login = () => {
  const { language, setLanguage, t } = useLanguage();
  const [theme, setTheme] = useState('light');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => {
    const savedTheme = localStorage.getItem('dio-theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialTheme = savedTheme ? savedTheme : (prefersDark ? 'dark' : 'light');
    setTheme(initialTheme);
    document.documentElement.classList.toggle('dark', initialTheme === 'dark');
  }, []);

  // F8 = ndërron temën (dark/light) – pa buton në ekran
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'F8') {
        e.preventDefault();
        setTheme((prev) => {
          const next = prev === 'dark' ? 'light' : 'dark';
          localStorage.setItem('dio-theme', next);
          document.documentElement.classList.toggle('dark', next === 'dark');
          return next;
        });
      }
      // F6 = ndërron gjuhën (sq / en) – pa flamuj në ekran
      if (e.key === 'F6') {
        e.preventDefault();
        setLanguage(language === 'sq' ? 'en' : 'sq');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setLanguage, language]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/login`, {
        username,
        password,
        remember
      });

      login(response.data.access_token, response.data.user);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      const isNetwork = !err.response || (err.message && String(err.message).toLowerCase().includes('network'));
      setError(isNetwork ? t('errorNetwork') : (err.response?.data?.detail || t('errorLogin')));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div
        className="bg"
        aria-hidden="true"
        style={{
          backgroundImage: theme === 'dark'
            ? `url('${PUBLIC}/assets/images/bg-dark.png')`
            : `url('${PUBLIC}/assets/images/bg-light.png')`
        }}
      />

      <div className="page">
        <section className="card" role="region" aria-label="Login">
          <div className="head">
            <div className="logo-wrap">
              <img
                className="logo-light"
                src={`${PUBLIC}/assets/images/logo-dio-iot.png`}
                alt="DIO IOT"
                onError={(e) => { e.target.onerror = null; e.target.src = `${PUBLIC}/assets/images/logo-full.svg`; }}
              />
              <img
                className="logo-dark"
                src={`${PUBLIC}/assets/images/logo-dio-iot-white.png`}
                alt="DIO IOT"
                onError={(e) => { e.target.onerror = null; e.target.src = `${PUBLIC}/assets/images/logo-white-full.svg`; }}
              />
            </div>
          </div>

          <div className="title">{t('loginTitle')}</div>
          <div className="subtitle">{t('loginSubtitle')}</div>

          {error && (
            <div className="errors">
              <ul>
                <li>{error}</li>
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} autoComplete="on" id="dioLoginForm">
            <div className="field">
              <label htmlFor="login">{t('username')}</label>
              <div className="input-wrap">
                <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeOpacity=".75" strokeWidth="2" strokeLinecap="round" />
                  <path d="M12 13a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z" stroke="currentColor" strokeOpacity=".75" strokeWidth="2" />
                </svg>
                <input
                  id="login"
                  type="text"
                  name="login"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoFocus
                  autoComplete="username"
                  placeholder={t('username')}
                />
              </div>
            </div>

            <div className="field">
              <label htmlFor="password">{t('passwordLabel')}</label>
              <div className="input-wrap">
                <svg className="icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M7 11V8a5 5 0 0 1 10 0v3" stroke="currentColor" strokeOpacity=".75" strokeWidth="2" strokeLinecap="round" />
                  <path d="M6 11h12a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2Z" stroke="currentColor" strokeOpacity=".75" strokeWidth="2" />
                </svg>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  placeholder={t('passwordLabel')}
                />
              </div>
            </div>

            <div className="row">
              <input
                id="remember"
                type="checkbox"
                name="remember"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
              />
              <span>{t('rememberCreds')}</span>
            </div>

            <button className="btn" type="submit" disabled={loading}>
              {loading ? t('loggingIn') : t('loginBtn')}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default Login;
